/**
 * ICE (enemy) content registry — the full roster.
 *
 * Design goals:
 *  - Every ICE telegraphs intent (perfect information) via labeled moves.
 *  - Distinct movesets: loops (predictable), weighted (variety), scripted (bosses).
 *  - Trace interaction: several ICE actively push the Trace clock; the 3 act
 *    bosses each warp the clock in a DIFFERENT way (see below).
 *  - HP ranges roll within [hpMin,hpMax] for variety without breaking determinism.
 *
 * Save-safety: AI is read from this static registry; only the live IceInstance
 * (defId + aiCursor + hp/statuses) is persisted, so scripted closures never hit
 * the save file. aiCursor doubles as the deterministic turn counter for loops.
 */

import type { IceDef, Intent } from "../types";

export const ICE_DEFS: Record<string, IceDef> = {
  // ===========================================================================
  // ACT 1 — fodder + early threats
  // ===========================================================================
  sentry: {
    id: "sentry",
    name: "Sentry",
    maxHp: 28,
    hpMin: 26,
    hpMax: 30,
    ai: {
      type: "loop",
      moves: [
        { intent: { kind: "attack", value: 7, hits: 1, label: "Strike" } },
        { intent: { kind: "defend", value: 6, label: "Harden" } },
      ],
    },
    description: "Standard patrol ICE. Predictable two-step loop.",
  },
  wisp: {
    id: "wisp",
    name: "Data Wisp",
    maxHp: 18,
    hpMin: 16,
    hpMax: 20,
    ai: {
      type: "loop",
      moves: [
        { intent: { kind: "attack", value: 3, hits: 2, label: "Flicker" } },
        {
          intent: {
            kind: "attackDebuff",
            value: 4,
            hits: 1,
            status: "weak",
            amount: 1,
            label: "Sap",
          },
        },
      ],
    },
    description: "Fragile harasser. Multi-strike and saps your output.",
  },
  leech: {
    id: "leech",
    name: "Leech",
    maxHp: 22,
    hpMin: 20,
    hpMax: 24,
    ai: {
      type: "loop",
      moves: [
        {
          intent: {
            kind: "attackDebuff",
            value: 5,
            status: "vulnerable",
            amount: 1,
            label: "Bite",
          },
        },
        { intent: { kind: "buff", status: "regen", amount: 3, label: "Feed" } },
      ],
    },
    description: "Bites to soften you, then heals itself. Kill it before it stabilizes.",
  },
  spider: {
    id: "spider",
    name: "Crawler",
    maxHp: 16,
    hpMin: 14,
    hpMax: 18,
    ai: {
      type: "loop",
      moves: [
        {
          intent: { kind: "debuff", status: "poison", amount: 3, label: "Venom" },
        },
        { intent: { kind: "attack", value: 4, label: "Skitter" } },
      ],
    },
    description: "Poisons on sight. Small but its venom adds up.",
  },
  glitcher: {
    id: "glitcher",
    name: "Glitcher",
    maxHp: 20,
    hpMin: 18,
    hpMax: 22,
    ai: {
      type: "weighted",
      moves: [
        { intent: { kind: "attack", value: 6, label: "Crash" }, weight: 2 },
        {
          intent: {
            kind: "debuff",
            status: "weak",
            amount: 2,
            label: "Scramble",
          },
          weight: 1,
        },
        { intent: { kind: "defend", value: 5, label: "Stutter" }, weight: 1 },
      ],
    },
    description: "Erratic. You never quite know which move is coming.",
  },
  probe: {
    id: "probe",
    name: "Probe",
    maxHp: 14,
    hpMin: 12,
    hpMax: 16,
    ai: {
      type: "loop",
      moves: [
        { intent: { kind: "trace", traceAmount: 2, label: "Scan" } },
        { intent: { kind: "attack", value: 5, label: "Zap" } },
      ],
    },
    tracePerTurn: 1,
    description: "Cheap recon drone. Bumps your Trace and chips away — kill it early.",
  },
  mauler: {
    id: "mauler",
    name: "Mauler",
    maxHp: 24,
    hpMin: 22,
    hpMax: 26,
    ai: {
      type: "loop",
      moves: [
        { intent: { kind: "buff", status: "strength", amount: 1, label: "Wind Up" } },
        { intent: { kind: "attack", value: 8, label: "Smash" } },
      ],
    },
    description: "Winds up, then smashes — its hits grow if you let the fight drag.",
  },
  warden_shard: {
    id: "warden_shard",
    name: "Warden Shard",
    maxHp: 30,
    hpMin: 28,
    hpMax: 34,
    innateStatus: { firewall: 1 },
    ai: {
      type: "loop",
      moves: [
        { intent: { kind: "defend", value: 8, label: "Shield" } },
        {
          intent: {
            kind: "attackDebuff",
            value: 6,
            status: "vulnerable",
            amount: 1,
            label: "Pierce",
          },
        },
      ],
    },
    description: "A splinter of the Warden. Hardened, and pries your defenses open.",
  },

  // ===========================================================================
  // ACT 2 — heavier patrols
  // ===========================================================================
  daemon: {
    id: "daemon",
    name: "Daemon",
    maxHp: 34,
    hpMin: 32,
    hpMax: 38,
    ai: {
      type: "weighted",
      noRepeat: false,
      moves: [
        { intent: { kind: "attack", value: 9, label: "Maul" }, weight: 3 },
        {
          intent: { kind: "buff", status: "strength", amount: 2, label: "Rage" },
          weight: 1,
        },
        { intent: { kind: "defend", value: 8, label: "Shell" }, weight: 1 },
      ],
    },
    tracePerTurn: 1,
    description: "Unpredictable brute. Generates extra Trace each turn.",
  },
  enforcer: {
    id: "enforcer",
    name: "Enforcer",
    maxHp: 40,
    hpMin: 38,
    hpMax: 44,
    ai: {
      type: "loop",
      moves: [
        { intent: { kind: "defend", value: 10, label: "Brace" } },
        { intent: { kind: "attack", value: 12, label: "Slam" } },
        {
          intent: {
            kind: "attackDebuff",
            value: 6,
            status: "vulnerable",
            amount: 2,
            label: "Pin",
          },
        },
      ],
    },
    description: "Heavy three-step: brace, then a big slam, then a softening pin.",
  },
  tracer: {
    id: "tracer",
    name: "Tracer",
    maxHp: 26,
    hpMin: 24,
    hpMax: 30,
    ai: {
      type: "loop",
      moves: [
        { intent: { kind: "trace", traceAmount: 4, label: "Pinpoint" } },
        { intent: { kind: "attack", value: 6, label: "Mark" } },
      ],
    },
    tracePerTurn: 1,
    description: "Lives to find you — spikes Trace hard. Prioritize it or race it.",
  },
  shade: {
    id: "shade",
    name: "Shade",
    maxHp: 30,
    hpMin: 28,
    hpMax: 34,
    ai: {
      type: "weighted",
      moves: [
        {
          intent: { kind: "attack", value: 7, hits: 2, label: "Double Tap" },
          weight: 2,
        },
        {
          intent: {
            kind: "debuff",
            status: "vulnerable",
            amount: 2,
            label: "Expose",
          },
          weight: 1,
        },
      ],
    },
    description: "Twin-strikes and exposes you for the next blow.",
  },
  swarmling: {
    id: "swarmling",
    name: "Swarmling",
    maxHp: 10,
    hpMin: 9,
    hpMax: 12,
    ai: {
      type: "loop",
      moves: [{ intent: { kind: "attack", value: 4, label: "Bite" } }],
    },
    description: "A summoned mote. Weak alone, lethal in numbers.",
  },

  // ===========================================================================
  // ACT 3 — lethal patrols
  // ===========================================================================
  reaper: {
    id: "reaper",
    name: "Reaper",
    maxHp: 48,
    hpMin: 46,
    hpMax: 54,
    ai: {
      type: "loop",
      moves: [
        {
          intent: {
            kind: "attackDebuff",
            value: 10,
            status: "weak",
            amount: 1,
            label: "Scythe",
          },
        },
        { intent: { kind: "buff", status: "strength", amount: 3, label: "Sharpen" } },
        { intent: { kind: "attack", value: 14, label: "Harvest" } },
      ],
    },
    tracePerTurn: 1,
    description: "Sharpens, then harvests. Its damage ramps fast — end it quickly.",
  },
  juggernaut: {
    id: "juggernaut",
    name: "Juggernaut",
    maxHp: 60,
    hpMin: 56,
    hpMax: 66,
    innateStatus: { firewall: 1 },
    ai: {
      type: "loop",
      moves: [
        { intent: { kind: "defend", value: 14, label: "Wall" } },
        { intent: { kind: "attack", value: 9, hits: 2, label: "Pound" } },
      ],
    },
    description: "Armored and relentless. Walls up, then pounds you twice.",
  },
  corruptor: {
    id: "corruptor",
    name: "Corruptor",
    maxHp: 38,
    hpMin: 36,
    hpMax: 42,
    ai: {
      type: "weighted",
      moves: [
        {
          intent: { kind: "debuff", status: "poison", amount: 5, label: "Infest" },
          weight: 2,
        },
        { intent: { kind: "attack", value: 8, label: "Lash" }, weight: 2 },
        {
          intent: { kind: "buff", status: "firewall", amount: 2, label: "Encrypt" },
          weight: 1,
        },
      ],
    },
    tracePerTurn: 1,
    description: "Stacks Poison on you and hardens itself. A war of attrition.",
  },

  // ===========================================================================
  // ELITES
  // ===========================================================================
  bulwark: {
    id: "bulwark",
    name: "Bulwark",
    maxHp: 46,
    hpMin: 44,
    hpMax: 52,
    isElite: true,
    innateStatus: { firewall: 2 },
    ai: {
      type: "loop",
      moves: [
        { intent: { kind: "attack", value: 6, hits: 2, label: "Slam" } },
        { intent: { kind: "trace", traceAmount: 3, label: "Triangulate" } },
        { intent: { kind: "defend", value: 12, label: "Fortify" } },
      ],
    },
    description: "Elite ICE. Hardened, and actively triangulates your signal.",
  },
  hive: {
    id: "hive",
    name: "Hive Node",
    maxHp: 40,
    hpMin: 38,
    hpMax: 46,
    isElite: true,
    ai: {
      type: "loop",
      moves: [
        { intent: { kind: "summon", summonDefId: "swarmling", label: "Spawn" } },
        { intent: { kind: "attack", value: 5, label: "Pulse" } },
        { intent: { kind: "summon", summonDefId: "swarmling", label: "Spawn" } },
      ],
    },
    tracePerTurn: 1,
    description: "Elite ICE. Spawns Swarmlings to bury you in chip damage.",
  },
  overseer: {
    id: "overseer",
    name: "Overseer",
    maxHp: 54,
    hpMin: 50,
    hpMax: 60,
    isElite: true,
    ai: {
      type: "loop",
      moves: [
        {
          intent: {
            kind: "attackDebuff",
            value: 8,
            status: "weak",
            amount: 2,
            label: "Suppress",
          },
        },
        { intent: { kind: "buff", status: "strength", amount: 2, label: "Empower" } },
        { intent: { kind: "attack", value: 10, label: "Execute" } },
        { intent: { kind: "trace", traceAmount: 4, label: "Lock On" } },
      ],
    },
    tracePerTurn: 1,
    description:
      "Elite ICE. Suppresses your damage, empowers itself, then executes — all while locking on.",
  },
  warbeast: {
    id: "warbeast",
    name: "Warbeast",
    maxHp: 64,
    hpMin: 60,
    hpMax: 72,
    isElite: true,
    ai: {
      type: "weighted",
      moves: [
        { intent: { kind: "attack", value: 16, label: "Rend" }, weight: 3 },
        {
          intent: { kind: "buff", status: "strength", amount: 3, label: "Fury" },
          weight: 1,
        },
        {
          intent: {
            kind: "attackDebuff",
            value: 8,
            status: "vulnerable",
            amount: 2,
            label: "Gore",
          },
          weight: 2,
        },
      ],
    },
    description: "Elite ICE. A pure damage threat that snowballs if the fight drags.",
  },

  // ===========================================================================
  // ACT BOSSES — each interacts with the Trace clock differently.
  // ===========================================================================

  // ACT 1 BOSS: The Warden — escalates its OWN power as Trace climbs (a soft
  // race). High Trace makes every move bigger. Beatable by efficient pressure.
  warden: {
    id: "warden",
    name: "The Warden",
    maxHp: 120,
    hpMin: 116,
    hpMax: 124,
    isBoss: true,
    innateStatus: { firewall: 1 },
    ai: {
      type: "scripted",
      script: (turn: number, trace: number): Intent => {
        const hot = trace >= 11; // tier 1+ Trace = enraged
        const cycle = (turn - 1) % 4;
        if (cycle === 0) {
          return { kind: "attack", value: hot ? 16 : 11, label: "Lockdown" };
        }
        if (cycle === 1) {
          return {
            kind: "attackDebuff",
            value: hot ? 10 : 7,
            status: "vulnerable",
            amount: 2,
            label: "Breach",
          };
        }
        if (cycle === 2) {
          return { kind: "defend", value: hot ? 20 : 14, label: "Reinforce" };
        }
        return {
          kind: "buff",
          status: "strength",
          amount: hot ? 3 : 2,
          label: "Escalate",
        };
      },
    },
    tracePerTurn: 1,
    description:
      "Act 1 boss. The Warden grows lethal as your Trace climbs — race the clock, don't turtle.",
  },

  // ACT 2 BOSS: Null Sovereign — actively DRIVES Trace up itself, then once the
  // clock is hot it stops attacking and just defends, forcing YOU to close out
  // before the hard countdown. A clock-pressure boss.
  sovereign: {
    id: "sovereign",
    name: "Null Sovereign",
    maxHp: 150,
    hpMin: 146,
    hpMax: 156,
    isBoss: true,
    ai: {
      type: "scripted",
      script: (turn: number, trace: number): Intent => {
        // Early: it pushes Trace aggressively. Late (hot): it walls up + hits,
        // daring you to finish before the countdown fires.
        const hot = trace >= 12;
        const cycle = (turn - 1) % 3;
        if (!hot) {
          if (cycle === 0) return { kind: "trace", traceAmount: 5, label: "Hunt" };
          if (cycle === 1) {
            return {
              kind: "attackDebuff",
              value: 9,
              status: "weak",
              amount: 1,
              label: "Sever",
            };
          }
          return { kind: "attack", value: 13, label: "Purge" };
        }
        // Hot phase: heavy block + a real hit. The Trace clock is your enemy now.
        if (cycle === 0) return { kind: "defend", value: 24, label: "Bastion" };
        if (cycle === 1) return { kind: "attack", value: 18, label: "Annihilate" };
        return {
          kind: "attackDebuff",
          value: 12,
          status: "vulnerable",
          amount: 2,
          label: "Condemn",
        };
      },
    },
    tracePerTurn: 2,
    description:
      "Act 2 boss. The Sovereign hunts your signal to spike Trace fast, then turtles — you must out-pace the hard countdown.",
  },

  // ACT 3 BOSS: ROOT — a two-phase final boss. Phase 1: spawns adds + debuffs.
  // Phase 2 (low HP): goes berserk AND adds extra Trace, turning the clock into
  // a true countdown. Interacts with Trace by accelerating it when wounded.
  root: {
    id: "root",
    name: "ROOT",
    maxHp: 200,
    hpMin: 196,
    hpMax: 206,
    isBoss: true,
    innateStatus: { firewall: 2 },
    ai: {
      type: "scripted",
      script: (turn: number, trace: number): Intent => {
        const cycle = (turn - 1) % 4;
        const hot = trace >= 14;
        // ROOT cycles a 4-beat pattern; when Trace is hot it swaps its weakest
        // beat for a Trace surge, threatening the hard countdown.
        if (cycle === 0) {
          return { kind: "summon", summonDefId: "swarmling", label: "Fork" };
        }
        if (cycle === 1) {
          return {
            kind: "attackDebuff",
            value: hot ? 16 : 12,
            status: "vulnerable",
            amount: 2,
            label: "Overwrite",
          };
        }
        if (cycle === 2) {
          return hot
            ? { kind: "trace", traceAmount: 6, label: "Cascade Trace" }
            : { kind: "defend", value: 18, label: "Recompile" };
        }
        return { kind: "attack", value: hot ? 22 : 15, hits: 1, label: "Delete" };
      },
    },
    tracePerTurn: 2,
    description:
      "Act 3 boss. ROOT forks copies of itself and, once your Trace runs hot, cascades it toward the hard countdown. The final wall.",
  },
};

export function getIceDef(id: string): IceDef {
  const def = ICE_DEFS[id];
  if (!def) throw new Error(`Unknown ICE def: ${id}`);
  return def;
}

/** All ICE ids (stable, sorted). */
export function allIceIds(): string[] {
  return Object.keys(ICE_DEFS).sort();
}
